import React, { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

function AccordionItem({ id, title, children, isOpen, onToggle }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        aria-expanded={isOpen}
        aria-controls={`panel-${id}`}
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
      >
        <span className="text-[15px] font-medium text-slate-800">{title}</span>
        <ChevronDownIcon
          className={`h-5 w-5 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>
      <div
        id={`panel-${id}`}
        role="region"
        aria-labelledby={`btn-${id}`}
        className={`px-4 pb-5 text-sm leading-6 text-slate-700 ${isOpen ? "block" : "hidden"}`}
      >
        {children}
      </div>
    </div>
  );
}

export default function CancellationRefundPolicyPage() {
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    const fromHash = window.location.hash?.replace("#", "");
    if (fromHash) setOpenId(fromHash);
  }, []);

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <h1 className="text-2xl font-semibold text-slate-900">Cancellation & Refund Policy</h1>
          <p className="mt-1 text-sm text-slate-600">Last updated on Nov 25 2025</p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-8 space-y-3">
        <AccordionItem
          id="policy-intro"
          title="Our Cancellation Philosophy"
          isOpen={openId === null || openId === "policy-intro"}
          onToggle={() => setOpenId(openId === "policy-intro" ? null : "policy-intro")}
        >
          <p>
            ShowTimeBro believes in helping its customers as far as possible and therefore follows a liberal cancellation policy. However, certain conditions and exceptions apply as detailed below.
          </p>
        </AccordionItem>

        <AccordionItem
          id="standard-cancellation"
          title="Standard Cancellation Window"
          isOpen={openId === "standard-cancellation"}
          onToggle={() => setOpenId(openId === "standard-cancellation" ? null : "standard-cancellation")}
        >
          <p>
            Cancellations will be considered only if the request is made within <strong>7 days of placing the order</strong>. Requests may not be accepted if the order has already been communicated to the vendor/merchant and the shipping process has been initiated.
          </p>
        </AccordionItem>

        <AccordionItem
          id="perishable-items"
          title="Non‑cancellable Items"
          isOpen={openId === "perishable-items"}
          onToggle={() => setOpenId(openId === "perishable-items" ? null : "perishable-items")}
        >
          <p>
            ShowTimeBro does not accept cancellation requests for perishable items such as flowers, food items, or other items where quality may degrade. Refunds or replacements may be provided only if the customer clearly establishes that the delivered product was defective or of unacceptable quality.
          </p>
        </AccordionItem>

        <AccordionItem
          id="damaged-defective"
          title="Damaged or Defective Product"
          isOpen={openId === "damaged-defective"}
          onToggle={() => setOpenId(openId === "damaged-defective" ? null : "damaged-defective")}
        >
          <p>
            If you receive a damaged or defective item, please report it to our Customer Service team within <strong>7 days of receipt</strong>. The request will be processed after verification by the merchant at their end.
          </p>
        </AccordionItem>

        <AccordionItem
          id="not-as-expected"
          title="Product Not as Expected"
          isOpen={openId === "not-as-expected"}
          onToggle={() => setOpenId(openId === "not-as-expected" ? null : "not-as-expected")}
        >
          <p>
            If you feel the product received is not as displayed on the site or does not meet expectations, you must notify our customer service team within <strong>7 days of delivery</strong>. After review, our team will decide the best resolution.
          </p>
        </AccordionItem>

        <AccordionItem
          id="warranty"
          title="Products with Manufacturer Warranty"
          isOpen={openId === "warranty"}
          onToggle={() => setOpenId(openId === "warranty" ? null : "warranty")}
        >
          <p>
            For products that include a manufacturer's warranty, customers are advised to contact the respective manufacturer directly for issues related to defects or repairs.
          </p>
        </AccordionItem>

        <AccordionItem
          id="refund-process"
          title="Refund Processing Time"
          isOpen={openId === "refund-process"}
          onToggle={() => setOpenId(openId === "refund-process" ? null : "refund-process")}
        >
          <p>
            Once a refund is approved by ShowTimeBro, the amount will be processed within <strong>6–8 business days</strong> and credited to the customer’s original payment method.
          </p>
        </AccordionItem>

        <AccordionItem
          id="contact"
          title="Contact & Support"
          isOpen={openId === "contact"}
          onToggle={() => setOpenId(openId === "contact" ? null : "contact")}
        >
          <p>If you have any concerns regarding cancellations or refunds, feel free to contact ShowTimeBro:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong>Email:</strong> <a className="text-amber-600 underline" href="mailto:info@showtimebro.com">info@showtimebro.com</a></li>
            <li><strong>Phone / WhatsApp:</strong> 7440550105</li>
          </ul>
        </AccordionItem>
      </div>

      <p className="mx-auto max-w-6xl px-4 mt-8 text-xs text-slate-500">
        Note: This Cancellation & Refund Policy applies to purchases made via ShowTimeBro. In cases involving third‑party sellers or event organizers, their specific policies may apply.
      </p>
    </main>
  );
}
